import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, UploadCloud } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { Progress } from "../ui/progress";

export const inputCls = "mt-1.5 w-full bg-transparent border border-white/15 px-3 py-3 text-sm focus:outline-none focus:border-[#1FD1FF]";
export const Label = ({ children }) => <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#A8B3C4]">{children}</span>;
const PasswordF = ({ label, ...p }) => {
  const [show, setShow] = useState(false);
  return (
    <label className="block"><Label>{label}</Label>
      <span className="relative block">
        <input {...p} type={show ? "text" : "password"} className={`${inputCls} pr-12`} autoCapitalize="none" autoCorrect="off" spellCheck={false} />
        <button type="button" onClick={() => setShow(!show)} aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
          data-testid={`${p["data-testid"]}-toggle`} className="absolute right-0 top-1.5 bottom-0 px-3 text-[#A8B3C4] hover:text-[#1FD1FF]">
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </span>
    </label>
  );
};
const GenreOptions = () => {
  const [g, setG] = useState([]);
  useEffect(() => { api.get("/genres").then((r) => setG(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);
  return <datalist id="genre-options">{g.map((x) => <option key={x.name} value={x.name} />)}</datalist>;
};
export const F = ({ label, ...p }) => (p.type === "password" ? <PasswordF label={label} {...p} /> : <label className="block"><Label>{label}</Label><input {...p} className={inputCls} /></label>);
export const Area = ({ label, ...p }) => <label className="block sm:col-span-2"><Label>{label}</Label><textarea rows={3} {...p} className={inputCls} /></label>;
export const FileF = ({ label, hint, ...p }) => (
  <label className="block"><Label>{label}</Label>
    <input type="file" {...p} className="mt-1.5 w-full border border-dashed border-white/20 px-3 py-4 text-sm file:mr-4 file:border-0 file:bg-[#1FD1FF] file:text-black file:px-3 file:py-1.5 file:font-mono file:text-xs file:uppercase" />
    {hint && <span className="block mt-1 text-xs text-[#A8B3C4]">{hint}</span>}
  </label>
);

export const UploadForm = ({ endpoint, method = "post", testid, children, onDone, label = "Subir", resetOnDone = true }) => {
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    setBusy(true);
    setProgress(0);
    try {
      await api.request({ url: endpoint, method, data: new FormData(formEl), onUploadProgress: (p) => setProgress(Math.round((p.loaded / (p.total || 1)) * 100)) });
      toast.success(method === "patch" ? "Cambios guardados" : "Subido correctamente");
      if (resetOnDone) formEl.reset();
      onDone?.();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-5">
      {children}
      <div className="sm:col-span-2 space-y-3">
        {busy && <Progress value={progress} data-testid={`${testid}-progress`} />}
        <button data-testid={testid} disabled={busy} className="flex items-center gap-2 px-6 py-3.5 bg-[#1FD1FF] text-black font-mono text-sm uppercase tracking-widest hover:bg-white transition-colors disabled:opacity-60">
          <UploadCloud className="w-4 h-4" />{busy ? `Enviando ${progress}%` : label}
        </button>
      </div>
    </form>
  );
};

export const MixFields = ({ d = {}, edit = false }) => (
  <>
    <F label="Título" name="title" required defaultValue={d.title} data-testid="mix-title-input" />
    <F label="Género" name="genre" required placeholder="Reggaetón" defaultValue={d.genre} data-testid="mix-genre-input" list="genre-options" autoComplete="off" />
    <GenreOptions />
    <F label="Etiquetas (separadas por coma)" name="tags" placeholder="dark, peak-time" defaultValue={(d.tags || []).join(", ")} data-testid="mix-tags-input" />
    <div className="grid grid-cols-2 gap-3">
      <F label="BPM" name="bpm" type="number" defaultValue={d.bpm ?? 128} data-testid="mix-bpm-input" />
      <F label="Duración" name="duration" placeholder="1:02:00" defaultValue={d.duration} data-testid="mix-duration-input" />
    </div>
    <div className="grid grid-cols-2 gap-3">
      <F label="Tonalidad" name="key" placeholder="C#m / 12A" defaultValue={d.key} data-testid="mix-key-input" autoComplete="off" />
      <F label="Fecha de lanzamiento" name="release_date" type="date" defaultValue={d.release_date || (d.created_at || "").slice(0, 10)} data-testid="mix-release-date-input" />
    </div>
    <Area label="Descripción" name="description" defaultValue={d.description} data-testid="mix-description-input" />
    <FileF label="Audio (MP3 / WAV)" name="audio" accept=".mp3,.wav,audio/*" required={!edit} hint={edit ? "Déjalo vacío para mantener el audio actual" : "Recomendado: MP3 320 kbps"} data-testid="mix-audio-input" />
    <FileF label="Portada (JPG / PNG)" name="cover" accept="image/*" hint={edit ? "Déjalo vacío para mantener la portada actual" : ""} data-testid="mix-cover-input" />
  </>
);

export const ToolFields = ({ d = {}, edit = false }) => (
  <>
    <F label="Título" name="title" required defaultValue={d.title} data-testid="tool-title-input" />
    <F label="Categoría" name="category" placeholder="Drum Kit" defaultValue={d.category} data-testid="tool-category-input" />
    <Area label="Descripción" name="description" defaultValue={d.description} data-testid="tool-description-input" />
    <FileF label="Archivo (ZIP / RAR / 7Z)" name="file" accept=".zip,.rar,.7z,.wav,.mp3" required={!edit} hint={edit ? "Déjalo vacío para mantener el archivo actual" : ""} data-testid="tool-file-input" />
  </>
);

export const FxFields = ({ d = {}, edit = false }) => (
  <>
    <F label="Nombre del pad" name="name" required defaultValue={d.name} data-testid="fx-name-input" />
    <label className="block"><Label>Color del pad</Label><input type="color" name="color" defaultValue={d.color || "#1FD1FF"} className="mt-1.5 w-full h-12 bg-transparent border border-white/15" data-testid="fx-color-input" /></label>
    <FileF label="Sonido (MP3 / WAV)" name="file" accept=".mp3,.wav,audio/*" required={!edit} hint={edit ? "Déjalo vacío para mantener el sonido actual" : ""} data-testid="fx-file-input" />
  </>
);
